
import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { Palette, Pipette } from "lucide-react";
import { CustomButton } from "./custom-button";

interface EnhancedColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  onApplyToAll?: (color: string) => void;
  className?: string;
}

const COLOR_PRESETS = [
  "#000000", "#374151", "#6B7280", "#9CA3AF",
  "#EF4444", "#F97316", "#F59E0B", "#EAB308",
  "#22C55E", "#10B981", "#06B6D4", "#0EA5E9",
  "#3B82F6", "#6366F1", "#8B5CF6", "#A855F7",
  "#EC4899", "#F43F5E", "#84CC16", "#65A30D"
];

const GRADIENT_PRESETS = [
  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
  "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
];

export const EnhancedColorPicker = ({ 
  value, 
  onChange, 
  onApplyToAll,
  className 
}: EnhancedColorPickerProps) => {
  const [showPalette, setShowPalette] = useState(false);
  const [activeTab, setActiveTab] = useState<"colors" | "gradients">("colors");

  return (
    <div className={cn("relative", className)}>
      <div className="flex items-center gap-1">
        {/* Current Color Display */}
        <div 
          className="w-8 h-8 rounded-lg border-2 border-white shadow-md cursor-pointer relative overflow-hidden"
          style={{ background: value }}
          onClick={() => setShowPalette(!showPalette)}
        >
          <div className="absolute inset-0 bg-black/10 opacity-0 hover:opacity-100 transition-opacity" />
        </div>
        
        {/* Custom Color Input */}
        <input
          type="color"
          value={value.startsWith("#") ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-6 h-6 rounded cursor-pointer border opacity-0 absolute"
          title="Custom color picker"
        />
        
        {/* Palette Toggle */}
        <CustomButton
          size="sm"
          variant="ghost"
          onClick={() => setShowPalette(!showPalette)}
          className="h-8 w-8 p-0"
          title="Color palette"
        >
          <Palette size={14} />
        </CustomButton>
        
        {/* Apply to All */}
        {onApplyToAll && (
          <CustomButton
            size="sm"
            variant="outline"
            onClick={() => onApplyToAll(value)}
            className="h-8 px-2"
            title="Apply to all nodes"
          >
            <Pipette size={12} />
          </CustomButton>
        )}
      </div>

      {/* Color Palette Dropdown */}
      {showPalette && (
        <div className="absolute top-10 left-0 z-50 bg-white rounded-lg shadow-lg border p-3 min-w-64">
          {/* Tabs */}
          <div className="flex gap-1 mb-3">
            <button
              onClick={() => setActiveTab("colors")}
              className={cn(
                "px-3 py-1 text-xs rounded-md transition-colors",
                activeTab === "colors" 
                  ? "bg-primary text-primary-foreground" 
                  : "hover:bg-gray-100"
              )}
            >
              Colors
            </button>
            <button
              onClick={() => setActiveTab("gradients")}
              className={cn(
                "px-3 py-1 text-xs rounded-md transition-colors",
                activeTab === "gradients" 
                  ? "bg-primary text-primary-foreground" 
                  : "hover:bg-gray-100"
              )}
            >
              Gradients
            </button>
          </div>

          {/* Color Grid */}
          {activeTab === "colors" && (
            <div className="grid grid-cols-4 gap-2">
              {COLOR_PRESETS.map((color) => (
                <button
                  key={color}
                  onClick={() => {
                    onChange(color);
                    setShowPalette(false);
                  }}
                  className={cn(
                    "w-10 h-10 rounded-lg border-2 transition-all hover:scale-110",
                    value === color ? "border-primary" : "border-gray-200"
                  )}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          )}

          {/* Gradient Grid */}
          {activeTab === "gradients" && (
            <div className="grid grid-cols-2 gap-2">
              {GRADIENT_PRESETS.map((gradient, index) => (
                <button
                  key={index}
                  onClick={() => {
                    onChange(gradient);
                    setShowPalette(false);
                  }}
                  className="w-20 h-10 rounded-lg border-2 border-gray-200 transition-all hover:scale-105"
                  style={{ background: gradient }}
                  title={`Gradient ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
